// import React, { useState, useEffect } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import { auth, db } from "../firebase";
// import { doc, getDoc, setDoc, updateDoc, arrayUnion } from "firebase/firestore";
// import { signInWithEmailAndPassword } from "firebase/auth";
// import { Button, Typography, Box, CircularProgress, TextField } from "@mui/material";

// export default function JoinClassroom() {
//   const { collegeId, classroomId } = useParams();
//   const navigate = useNavigate();
//   const [loading, setLoading] = useState(true);
//   const [classData, setClassData] = useState(null);
//   const [error, setError] = useState(null);
//   const [joining, setJoining] = useState(false);

//   // Login state
//   const [email, setEmail] = useState("");
//   const [password, setPassword] = useState("");
//   const [loggingIn, setLoggingIn] = useState(false);

//   useEffect(() => {
//     const fetchClassroom = async () => {
//       try {
//         // Fetch class details
//         const classRef = doc(db, "colleges", collegeId, "classes", classroomId);
//         const classSnap = await getDoc(classRef);

//         if (!classSnap.exists()) {
//           setError("Class not found.");
//           setLoading(false);
//           return;
//         }

//         setClassData(classSnap.data());
//       } catch (err) {
//         console.error("Error fetching class:", err);
//         setError("Failed to load class data.");
//       }
//       setLoading(false);
//     };

//     fetchClassroom();
//   }, [collegeId, classroomId]);

//   const handleLogin = async () => {
//     setLoggingIn(true);
//     try {
//       await signInWithEmailAndPassword(auth, email, password);
//     } catch (err) {
//       setError("Invalid email or password.");
//       console.error("Login error:", err);
//     }
//     setLoggingIn(false);
//   };

//   const handleJoinClass = async () => {
//     if (!auth.currentUser || !classData) return;

//     setJoining(true);
//     const studentId = auth.currentUser.uid;
//     const studentRef = doc(db, "colleges", collegeId, "students", studentId);
//     const classRef = doc(db, "colleges", collegeId, "classes", classroomId);
//     const classStudentRef = doc(db, "colleges", collegeId, "classes", classroomId, "students", studentId);

//     try {
//       // Add student entry under class's students
//       await setDoc(classStudentRef, { studentId }, { merge: true });

//       // Add joinedClasses array under student's record
//       await setDoc(studentRef, { joinedClasses: arrayUnion(classroomId) }, { merge: true });

//       // Update class to include this student
//       await updateDoc(classRef, { students: arrayUnion(studentId) });

//       navigate(`/student/${collegeId}`);
//     } catch (err) {
//       console.error("Error joining class:", err);
//       setError("Failed to join class.");
//     }
//     setJoining(false);
//   };

//   if (loading) return <CircularProgress />;
//   if (error) return <Typography color="error">{error}</Typography>;

//   return (
//     <Box sx={{ maxWidth: 400, mx: "auto", mt: 4, p: 2, border: "1px solid #ccc", borderRadius: 2 }}>
//       <Typography variant="h5">Join Class: {classData?.name}</Typography>

//       {auth.currentUser ? (
//         <Button variant="contained" onClick={handleJoinClass} disabled={joining} sx={{ mt: 2 }}>
//           {joining ? "Joining..." : "Join Class"}
//         </Button>
//       ) : (
//         <Box sx={{ mt: 2 }}>
//           <Typography variant="h6">Login to Join</Typography>
//           <TextField
//             fullWidth
//             label="Email"
//             variant="outlined"
//             value={email}
//             onChange={(e) => setEmail(e.target.value)}
//             sx={{ mt: 1 }}
//           />
//           <TextField
//             fullWidth
//             label="Password"
//             type="password"
//             variant="outlined"
//             value={password}
//             onChange={(e) => setPassword(e.target.value)}
//             sx={{ mt: 1 }}
//           />
//           <Button variant="contained" onClick={handleLogin} disabled={loggingIn} sx={{ mt: 2 }}>
//             {loggingIn ? "Logging in..." : "Login"}
//           </Button>
//         </Box>
//       )}
//     </Box>
//   );
// }

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { auth, db } from "../../config/firebase";
import { doc, getDoc, setDoc, updateDoc, arrayUnion } from "firebase/firestore";
import { 
  Button, 
  Typography, 
  Box, 
  CircularProgress, 
  Card, 
  CardContent, 
  Fade, 
  Zoom, 
  Alert,
  Avatar,
  Divider
} from "@mui/material";
import { CheckCircle, ClassOutlined } from "@mui/icons-material";
import { motion } from "framer-motion";

export default function JoinClassroom() {
  const { collegeId, classroomId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [classData, setClassData] = useState(null);
  const [error, setError] = useState(null);
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);

  useEffect(() => {
    const fetchClassroom = async () => {
      try {
        // Check if user is logged in
        if (!auth.currentUser) {
          navigate('/login', { state: { from: window.location.pathname } });
          return;
        }

        // Check if student is already in this class
        const studentRef = doc(db, "colleges", collegeId, "students", auth.currentUser.uid);
        const studentSnap = await getDoc(studentRef);
        
        if (studentSnap.exists() && studentSnap.data().joinedClasses?.includes(classroomId)) {
          setJoined(true);
        }

        // Fetch class details
        const classRef = doc(db, "colleges", collegeId, "classes", classroomId);
        const classSnap = await getDoc(classRef);

        if (!classSnap.exists()) {
          setError("Class not found.");
          return;
        }

        setClassData(classSnap.data());
      } catch (err) {
        console.error("Error:", err);
        setError("Failed to load class data.");
      } finally {
        setLoading(false);
      }
    };

    fetchClassroom();
  }, [collegeId, classroomId, navigate]);

  const handleJoinClass = async () => {
    if (!auth.currentUser || !classData) return;

    setJoining(true);
    setError(null);
    const studentId = auth.currentUser.uid;
    const studentRef = doc(db, "colleges", collegeId, "students", studentId);
    const classRef = doc(db, "colleges", collegeId, "classes", classroomId);
    const classStudentRef = doc(db, "colleges", collegeId, "classes", classroomId, "students", studentId);

    try {
      // Batch these operations if you prefer atomic writes
      await Promise.all([
        setDoc(classStudentRef, { studentId }, { merge: true }),
        setDoc(studentRef, { joinedClasses: arrayUnion(classroomId) }, { merge: true }),
        updateDoc(classRef, { students: arrayUnion(studentId) })
      ]);

      // Success state
      setJoined(true);
      
      // Auto-navigate after 2 seconds
      setTimeout(() => {
        navigate(`/student/${collegeId}`);
      }, 2000);
    } catch (err) {
      console.error("Error joining class:", err);
      setError("Failed to join class. Please try again.");
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          {error}4
        </Alert>
      </Box>
    );
  }

  return (
    <Fade in={true} timeout={500}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        p: 2,
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
      }}>
        <Zoom in={true}>
          <Card sx={{
            maxWidth: 500,
            width: '100%',
            borderRadius: 3,
            boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            <Box sx={{
              background: 'linear-gradient(45deg, #3f51b5 0%, #2196f3 100%)',
              color: 'white',
              p: 3,
              textAlign: 'center'
            }}>
              <Avatar sx={{ 
                bgcolor: 'white', 
                color: '#3f51b5', 
                width: 60, 
                height: 60,
                mx: 'auto',
                mb: 2
              }}>
                <ClassOutlined fontSize="large" />
              </Avatar>
              <Typography variant="h5" fontWeight="bold">
                {classData?.name}
              </Typography>
              <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                {classData?.subject} • {classData?.instructorName}
              </Typography>
            </Box>

            <CardContent sx={{ p: 4 }}>
              {joined ? (
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <CheckCircle sx={{ fontSize: 60, color: '#4caf50', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      Successfully Joined!
                    </Typography>
                    <Typography color="text.secondary">
                      You're now part of this classroom.
                    </Typography>
                    <CircularProgress size={24} sx={{ mt: 3 }} />
                  </Box>
                </motion.div>
              ) : (
                <>
                  <Typography variant="body1" paragraph>
                    You're about to join <strong>{classData?.name}</strong> taught by {classData?.instructorName}.
                  </Typography>
                  
                  <Divider sx={{ my: 3 }} />
                  
                  <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <motion.div whileHover={{ scale: 1.02 }}>
                      <Button
                        variant="contained"
                        size="large"
                        onClick={handleJoinClass}
                        disabled={joining}
                        sx={{
                          px: 6,
                          py: 1.5,
                          fontSize: '1rem',
                          borderRadius: 2,
                          boxShadow: 'none',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(63, 81, 181, 0.3)'
                          }
                        }}
                      >
                        {joining ? (
                          <>
                            <CircularProgress size={24} sx={{ mr: 1, color: 'white' }} />
                            Joining...
                          </>
                        ) : (
                          'Join Classroom'
                        )}
                      </Button>
                    </motion.div>
                  </Box>
                </>
              )}

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Alert severity="error" sx={{ mt: 3 }}>
                    {error}
                  </Alert>
                </motion.div>
              )}
            </CardContent>
          </Card>
        </Zoom>
      </Box>
    </Fade>
  );
}
